import React from "react";
import FormInput from "@/components/FormInput";
import { Control, Controller, useWatch } from "react-hook-form";
import { Box, Checkbox, Typography } from "@mui/material";
import BatteryModelsForm from "@/pages/admin-pages/UserManagementPage/components/CreateUserModal/BatteryModelsForm.tsx";
import FormAsyncSelect from "@/components/FormAsyncSelect";
import { getManufacturingBrandReq } from "@/api/userApi";
import { EmailPatternValidation } from "@/validators/emailPatternValidation.ts";
import { validatePhone } from "@/utils/phoneValidation.ts";
import { PHONE_NUMBER_REGEX } from "@/constants/phoneValidation";

const SecondOemForm = ({
  control,
  isEdit = false,
  disabled = false,
}: {
  control: Control;
  isEdit?: boolean;
  disabled?: boolean;
}) => {
  const termsAndConditions = useWatch({ control, name: "termsAndConditions" });

  return (
    <Box sx={{ height: "100%", overflow: isEdit ? "initial" : "auto" }}>
      <FormAsyncSelect
        name={"manufactoringBrand"}
        control={control}
        label={"Manufacturing Brand"}
        placeholder={"Select manufacturer"}
        getItems={(searchValue) => getManufacturingBrandReq(searchValue)}
        itemsKey={"oems"}
        disabled={disabled}
        controlProps={{ rules: { required: "Manufacturing is required" } }}
      />

      <Controller
        name={"termsAndConditions"}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Box
            sx={{
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              gap: "4px",
              marginBottom: "16px",
              alignItems: "center",
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={() => !disabled && onChange(!value)}
          >
            <Checkbox
              sx={{ color: "#0076CE" }}
              checked={!!value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
            />
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                T&Cs are required
              </Typography>
              {error && (
                <Typography
                  sx={{
                    color: "error.main",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {error.message}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      />

      <FormInput
        name={"contactName"}
        control={control}
        label={"Contact Name(s)"}
        placeholder={"John Smith"}
        disabled={disabled}
        controlProps={{ rules: { required: "Contact Name is required" } }}
      />
      <FormInput
        name={"contactEmail"}
        control={control}
        label={"Contact email(s)"}
        placeholder={"email@gmail.com"}
        disabled={disabled}
        controlProps={{
          rules: {
            required: "Contact email is required",
            ...EmailPatternValidation,
          },
        }}
      />
      <FormInput
        name={"contactPhone"}
        control={control}
        label={"Contact Phone"}
        placeholder={"(XXX) XXX-XXXX"}
        disabled={disabled}
        controlProps={{
          rules: {
            validate: validatePhone,
          },
        }}
        inputProps={{
          maxLength: 14,
          onKeyPress: (e) => {
            if (!PHONE_NUMBER_REGEX.test(e.key)) {
              e.preventDefault();
            }
          },
        }}
      />
      <BatteryModelsForm control={control} disabled={disabled} />
      <FormInput
        name={"dermsList"}
        control={control}
        label={"If integrated with existing DERMs, please list here:"}
        placeholder={"DERMs..."}
        disabled={disabled}
      />
      <FormInput
        name={"apiIntegrations"}
        control={control}
        label={"Current API Integrations"}
        placeholder={"APIs, comma separated"}
        disabled={disabled}
      />
      <Controller
        name={"ieeeProtocol"}
        control={control}
        render={({ field: { onChange, value } }) => {
          return (
            <Box
              onChange={(value) => onChange(value)}
              sx={{
                cursor: disabled ? "not-allowed" : "pointer",
                display: "flex",
                gap: "4px",
                marginBottom: "16px",
                alignItems: "center",
                opacity: disabled ? 0.6 : 1,
              }}
              onClick={() => !disabled && onChange(!value)}
            >
              <Checkbox
                sx={{ color: "#0076CE" }}
                checked={!!value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
              />
              <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                The battery support IEEE 2030.5 protocol
              </Typography>
            </Box>
          );
        }}
      />
      <FormInput
        name={"maintainingTelemetryDataInterval"}
        control={control}
        label={
          "At what interval does the BESS Operator maintain telemetry data? "
        }
        placeholder={"e.g. 15 minutes"}
        disabled={disabled}
      />
      <FormInput
        name={"achInformation"}
        control={control}
        label={"ACH Information"}
        placeholder={"ACH Information"}
        disabled={disabled}
      />
    </Box>
  );
};

export default SecondOemForm;
